/**
 * PDF optimizer for the web.
 *
 * A published PDF is fetched page-by-page by every reader, so its bytes are
 * paid for twice: once at upload and again on every later view. This rewrites
 * the embedded page images at a target resolution and repacks the file with
 * object streams.
 *
 * It is deliberately conservative. Every image it does not fully understand is
 * left byte-for-byte as the author wrote it, a re-encoded image is only used
 * when it is meaningfully smaller, and the finished document is parsed again
 * before its bytes are offered. A compressor that can corrupt a post is worse
 * than no compressor at all, so any doubt returns the original file untouched.
 */
import {
  decodePDFRawStream,
  PDFArray,
  PDFBool,
  type PDFDict,
  PDFDocument,
  PDFName,
  PDFNumber,
  type PDFObject,
  PDFRawStream,
  type PDFRef,
} from 'pdf-lib'

import {logger} from '#/logger'
import {
  chooseBytes,
  type ImagePlan,
  minSavingFor,
  type OptimizeLevel,
  type OptimizeOptions,
  type OptimizeResult,
  type PdfImageInfo,
  planImage,
  shouldReplaceImage,
} from './optimize'
import {countPdfPages} from './pdf.web'

/**
 * Keep every canvas under the limits browsers impose (Safari refuses past
 * 4096px). No preset asks for more than this, so it is a guard, not a policy.
 */
const MAX_CANVAS_EDGE = 4096

/**
 * Stop rewriting images after this long and save what we have. A 150-page scan
 * can hold hundreds of images, and an upload that appears to hang is worse than
 * one that is merely less small — the biggest images are handled first, so the
 * bulk of the saving is already in by the time this fires.
 */
const DEFAULT_TIME_BUDGET_MS = 20_000

type ImageRecord = {
  ref: PDFRef
  stream: PDFRawStream
  info: PdfImageInfo
  plan: ImagePlan
}

function nameValue(object: PDFObject | undefined): string | undefined {
  return object instanceof PDFName
    ? object.asString().replace(/^\//, '')
    : undefined
}

function numberValue(object: PDFObject | undefined): number | undefined {
  if (!(object instanceof PDFNumber)) return undefined
  const value = object.asNumber()
  return Number.isFinite(value) ? value : undefined
}

function isTrue(object: PDFObject | undefined): boolean {
  return object instanceof PDFBool && object.asBoolean()
}

/** `/Filter` is a name or an array of names; no key at all means raw samples. */
function filterNames(dict: PDFDict): string[] {
  const filter = dict.get(PDFName.of('Filter'))
  if (filter instanceof PDFName) {
    const name = nameValue(filter)
    return name ? [name] : []
  }
  if (filter instanceof PDFArray) {
    const names: string[] = []
    for (const entry of filter.asArray()) {
      const name = nameValue(entry)
      if (name) names.push(name)
    }
    return names
  }
  return []
}

function readImageInfo(dict: PDFDict): PdfImageInfo | null {
  const width = numberValue(dict.get(PDFName.of('Width')))
  const height = numberValue(dict.get(PDFName.of('Height')))
  if (!width || !height || width < 1 || height < 1) return null
  return {
    filters: filterNames(dict),
    bitsPerComponent:
      numberValue(dict.get(PDFName.of('BitsPerComponent'))) ?? 1,
    colorSpace: nameValue(dict.get(PDFName.of('ColorSpace'))) ?? 'Unknown',
    width: Math.round(width),
    height: Math.round(height),
    imageMask: isTrue(dict.get(PDFName.of('ImageMask'))),
    hasMask: dict.has(PDFName.of('Mask')),
    hasSoftMask: dict.has(PDFName.of('SMask')),
    hasDecodeParms:
      dict.has(PDFName.of('DecodeParms')) || dict.has(PDFName.of('DP')),
  }
}

/**
 * Every indirect object that is an image XObject, with the level's verdict on
 * it already attached. Images that are direct objects (rare) are not seen here,
 * which simply leaves them alone.
 */
function collectImageRecords(
  doc: PDFDocument,
  level: OptimizeLevel,
): ImageRecord[] {
  const records: ImageRecord[] = []
  for (const [ref, object] of doc.context.enumerateIndirectObjects()) {
    if (!(object instanceof PDFRawStream)) continue
    if (nameValue(object.dict.get(PDFName.of('Subtype'))) !== 'Image') continue
    const info = readImageInfo(object.dict)
    if (!info) continue
    records.push({ref, stream: object, info, plan: planImage(info, level)})
  }
  return records
}

/** Decodes an image to something a canvas can draw. */
async function decodeImage(
  record: ImageRecord,
): Promise<ImageBitmap | ImageData> {
  const {stream, info} = record
  if (info.filters[0] === 'DCTDecode') {
    // Copy into a plain ArrayBuffer: the stream's buffer may be a shared one,
    // which `Blob` will not accept.
    const copy = new ArrayBuffer(stream.contents.byteLength)
    new Uint8Array(copy).set(stream.contents)
    return await createImageBitmap(new Blob([copy], {type: 'image/jpeg'}))
  }
  const samples =
    info.filters[0] === 'FlateDecode'
      ? decodePDFRawStream(stream).decode()
      : stream.contents
  const components = info.colorSpace === 'DeviceGray' ? 1 : 3
  const needed = info.width * info.height * components
  if (samples.length < needed) {
    throw new Error('Image samples are shorter than its dimensions')
  }
  // A canvas needs RGBA; grey expands to three equal channels.
  const rgba = new Uint8ClampedArray(info.width * info.height * 4)
  for (let src = 0, dst = 0; dst < rgba.length; src += components, dst += 4) {
    const value = samples[src]
    rgba[dst] = value
    rgba[dst + 1] = components === 1 ? value : samples[src + 1]
    rgba[dst + 2] = components === 1 ? value : samples[src + 2]
    rgba[dst + 3] = 255
  }
  return new ImageData(rgba, info.width, info.height)
}

/** Draws `source` at the target size and encodes it as a JPEG. */
async function encodeJpeg(
  source: ImageBitmap | ImageData,
  width: number,
  height: number,
  quality: number,
): Promise<Uint8Array | null> {
  const bitmap = await createImageBitmap(source, {
    resizeWidth: width,
    resizeHeight: height,
    resizeQuality: 'high',
  })
  let canvas: HTMLCanvasElement | undefined
  try {
    canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d', {alpha: false})
    if (!ctx) return null
    ctx.drawImage(bitmap, 0, 0)
    const blob = await new Promise<Blob | null>(resolve =>
      canvas!.toBlob(resolve, 'image/jpeg', quality),
    )
    if (!blob) return null
    return new Uint8Array(await blob.arrayBuffer())
  } finally {
    bitmap.close()
    if (canvas) {
      // Zeroing the dimensions is the only reliable way to tell the browser it
      // can reclaim the backing store.
      canvas.width = 0
      canvas.height = 0
    }
  }
}

/**
 * Swaps the image's samples for the new JPEG, in place. The object keeps its
 * reference, so every content stream that draws it keeps working, and only the
 * keys describing the old encoding are replaced.
 */
function replaceImage(
  doc: PDFDocument,
  record: ImageRecord,
  jpeg: Uint8Array,
): void {
  if (record.plan.action !== 'jpeg') return
  const dict = record.stream.dict
  dict.set(PDFName.of('Width'), PDFNumber.of(record.plan.width))
  dict.set(PDFName.of('Height'), PDFNumber.of(record.plan.height))
  dict.set(PDFName.of('ColorSpace'), PDFName.of('DeviceRGB'))
  dict.set(PDFName.of('BitsPerComponent'), PDFNumber.of(8))
  dict.set(PDFName.of('Filter'), PDFName.of('DCTDecode'))
  // The original samples are gone, so anything describing them has to go too.
  dict.delete(PDFName.of('DecodeParms'))
  dict.delete(PDFName.of('DP'))
  doc.context.assign(record.ref, PDFRawStream.of(dict, jpeg))
}

function untouched(input: Uint8Array): OptimizeResult {
  return {
    bytes: input,
    applied: false,
    originalSize: input.length,
    optimizedSize: input.length,
    imagesRewritten: 0,
    imagesSkipped: 0,
  }
}

export async function optimizePdf(
  input: Uint8Array,
  level: OptimizeLevel,
  {
    expectedPageCount,
    signal,
    onProgress,
    timeBudgetMs = DEFAULT_TIME_BUDGET_MS,
  }: OptimizeOptions & {timeBudgetMs?: number} = {},
): Promise<OptimizeResult> {
  if (signal?.aborted) return untouched(input)

  let doc: PDFDocument
  try {
    doc = await PDFDocument.load(input, {updateMetadata: false})
  } catch (e) {
    logger.warn('Could not parse a PDF to optimize it', {safeMessage: e})
    return untouched(input)
  }

  const records = collectImageRecords(doc, level)
  // Biggest first: the largest images give the biggest saving, so a run cut
  // short by the time budget still keeps the useful part.
  const planned = records
    .filter(record => record.plan.action === 'jpeg')
    .sort((a, b) => b.stream.contents.length - a.stream.contents.length)
  const deadline = Date.now() + timeBudgetMs
  let rewritten = 0

  for (let index = 0; index < planned.length; index++) {
    if (signal?.aborted) return untouched(input)
    if (Date.now() > deadline) {
      logger.debug('PDF optimization hit its time budget', {
        considered: index,
        total: planned.length,
      })
      break
    }
    const record = planned[index]
    if (record.plan.action !== 'jpeg') continue
    // No preset asks for a canvas this large, so this only catches a preset
    // that was changed into something a browser cannot draw.
    if (Math.max(record.plan.width, record.plan.height) > MAX_CANVAS_EDGE) {
      continue
    }
    let source: ImageBitmap | ImageData | undefined
    try {
      source = await decodeImage(record)
      const jpeg = await encodeJpeg(
        source,
        record.plan.width,
        record.plan.height,
        record.plan.quality,
      )
      if (
        jpeg &&
        shouldReplaceImage(record.stream.contents.length, jpeg.length, {
          minSaving: minSavingFor(record.info),
        })
      ) {
        replaceImage(doc, record, jpeg)
        rewritten += 1
      }
    } catch (e) {
      // An image we cannot decode is left exactly as it was.
      logger.debug('Left a PDF image untouched', {
        filter: record.info.filters[0],
        safeMessage: e,
      })
    } finally {
      if (source instanceof ImageBitmap) source.close()
    }
    onProgress?.((index + 1) / planned.length)
  }

  if (signal?.aborted) return untouched(input)

  let output: Uint8Array
  try {
    output = await doc.save({
      /*
       * Deliberately *not* object streams, though they are the smaller file.
       *
       * PDF 1.5 object streams hide the page tree inside a compressed stream,
       * and the page counter the upload endpoint uses reads the object graph as
       * plain text — it would see the whole document as one page. That number
       * is what the record stores and what the reader shows before the file
       * loads, and it is the same number the server enforces its page cap with,
       * so a document saved that way ships an under-counted record and slips
       * past the cap. Writing the page tree plainly costs about 2% of the file
       * on a scanned deck and buys back both.
       */
      useObjectStreams: false,
      addDefaultPage: false,
      updateFieldAppearances: false,
    })
  } catch (e) {
    logger.warn('Could not save an optimized PDF', {safeMessage: e})
    return untouched(input)
  }

  // The rewrite is only trustworthy if the document still parses to the same
  // number of pages, so the output is read back before it is offered.
  if (expectedPageCount !== undefined) {
    try {
      const pages = await countPdfPages(output)
      if (pages !== expectedPageCount) {
        logger.warn(
          'Optimized PDF changed its page count; keeping the original',
          {
            expectedPageCount,
            pages,
          },
        )
        return untouched(input)
      }
    } catch (e) {
      logger.warn('Could not verify an optimized PDF; keeping the original', {
        safeMessage: e,
      })
      return untouched(input)
    }
  }

  const imagesSkipped = records.length - rewritten
  const {bytes, used} = chooseBytes(input, output)
  if (!used) {
    return {...untouched(input), imagesRewritten: rewritten, imagesSkipped}
  }
  logger.debug('Optimized a PDF', {
    originalSize: input.length,
    optimizedSize: bytes.length,
    imagesRewritten: rewritten,
    imagesSkipped,
    level,
  })
  return {
    bytes,
    applied: true,
    originalSize: input.length,
    optimizedSize: bytes.length,
    imagesRewritten: rewritten,
    imagesSkipped,
  }
}
